import { useState, useEffect } from 'react';
import { Scissors, ImageIcon, Loader2 } from 'lucide-react';
import { isTrimmed, cropAlternativeOf } from './receiptCrop';

const REASON_COPY = {
  pdf: null,
  failed: 'Trimming unavailable — using the original photo.',
};

/** Large preview of the chosen photo with a trimmed / original switch. */
export function CropReview({ file, busy, onSwap }) {
  const [url, setUrl] = useState(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    if (!file) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u); setBroken(false);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  if (!file || file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')) return null;

  const trimmed = isTrimmed(file);
  const alt = cropAlternativeOf(file);
  const note = alt ? null : (REASON_COPY[file.cropReason] ?? 'No clear paper edges found — using the original photo.');
  const seg = (active) => `py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95 ${active ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#2A3240] text-gray-300 border border-white/10 hover:border-[#F5A623]/40'}`;

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#161B24]" data-testid="crop-review">
      <div className="relative flex items-center justify-center bg-black/40 min-h-[180px]">
        {url && !broken ? (
          <img src={url} alt="" onError={() => setBroken(true)} className="max-h-[50vh] w-auto max-w-full object-contain" data-testid="crop-review-image" />
        ) : (
          <div className="py-16 text-gray-500 text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Preview not available</div>
        )}
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${trimmed ? 'bg-emerald-500/90 text-white' : 'bg-black/60 text-gray-200'}`} data-testid="crop-review-badge">
          {trimmed ? <><Scissors className="w-3 h-3" /> Background trimmed</> : 'Original photo'}
        </span>
        {busy && (
          <span className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-black/70 text-[#F5A623] text-[11px] flex items-center gap-1.5" data-testid="crop-review-busy">
            <Loader2 className="w-3 h-3 animate-spin" /> Trimming…
          </span>
        )}
      </div>
      {alt ? (
        <div className="grid grid-cols-2 gap-2 p-2">
          <button type="button" onClick={() => trimmed && onSwap(file, alt)} className={seg(!trimmed)} data-testid="crop-use-original-btn"><ImageIcon className="w-4 h-4" /> Use original</button>
          <button type="button" onClick={() => !trimmed && onSwap(file, alt)} className={seg(trimmed)} data-testid="crop-use-trimmed-btn"><Scissors className="w-4 h-4" /> Use trimmed</button>
        </div>
      ) : note && !busy ? (
        <p className="px-3 py-2 text-[11px] text-gray-400" data-testid="crop-review-note">{note}</p>
      ) : null}
    </div>
  );
}
